import { createAction, props } from '@ngrx/store'
import { Update } from '@ngrx/entity'

import {
  RentalBaseRate,
  Rental,
  RentalCheckTime,
  RentalStatus,
  RentalSecurityDepositConfig,
  TaxV3,
  TokeetImage,
  TokeetImagePayload,
  RentalMultiUnitSettings,
} from './rental.models'
import {
  CreateRentalPayload,
  UpdateRentalCustomPayload,
  UpdateRentalDetailsPayload,
  UpdateRentalPayload,
  UpdateSubunitRentalPayload,
  UpdateRentalInstructionsPayload,
  UpdateRentalBaseRateSettingsPayload,
} from './rental.interfaces'
import { RentalPaymentSchedule } from './schedule.model'

export const loadRentals = createAction('[Rental] Load Rentals', props<{ less?: boolean }>())
export const loadRentalsComplete = createAction('[Rental] Load Rentals Complete', props<{ rentals: Rental[] }>())

export const loadRentalRates = createAction('[Rental] Load Rental Rates', props<{ pkeys?: string[] }>())
export const loadRentalRatesComplete = createAction(
  '[Rental] Load Rental Rates Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const loadRentalsPrimaryImages = createAction('[Rental] Load Primary Images', props<{ rentalIds?: string[] }>())
export const loadRentalsPrimaryImagesComplete = createAction(
  '[Rental] Load Primary Images Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const loadRental = createAction('[Rental] Load Details', props<{ id: string }>())
export const loadRentalComplete = createAction('[Rental] Load Details Complete', props<{ rental: Rental }>())

export const loadRentalImages = createAction('[Rental] Load Images', props<{ id: string }>())
export const loadRentalImagesComplete = createAction(
  '[Rental] Load Images Complete',
  props<{ update: Update<Rental> }>()
)

export const loadRentalsImageCounts = createAction('[Rental] Load Image Counts', props<{ ids: string[] }>())
export const loadRentalsImageCountsComplete = createAction(
  '[Rental] Load Image Counts Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const loadRentalsImages = createAction('[Rental] Load Images By Rentals', props<{ ids: string[] }>())
export const loadRentalsImagesComplete = createAction(
  '[Rental] Load Images By Rentals Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const deleteRental = createAction('[Rental] Delete', props<{ rentalId: string; redirectPath?: string }>())
export const deleteRentalComplete = createAction('[Rental] Delete Complete', props<{ id: string }>())

export const addRental = createAction('[Rental] Add', props<{ payload: CreateRentalPayload }>())
export const addRentalComplete = createAction('[Rental] Add Complete', props<{ rental: Rental }>())

export const addSubUnitRentals = createAction('[Rental] Add Subunits', props<{ items: CreateRentalPayload[] }>())
export const addSubUnitRentalsComplete = createAction('[Rental] Add Subunits Complete', props<{ rentals: Rental[] }>())

export const loadArchivedRentals = createAction('[Rental] Load Archived')
export const loadArchivedRentalsComplete = createAction(
  '[Rental] Load Archived Complete',
  props<{ rentals: Rental[] }>()
)

export const archiveRental = createAction('[Rental] Archive', props<{ rental: Rental }>())
export const archiveRentalComplete = createAction('[Rental] Archive Complete', props<{ rental: Rental }>())

export const setRentalChildren = createAction(
  '[Rental] set rental children',
  props<{ rentalId: string; addedChildren: string[]; removedChildren: string[]; silent?: boolean }>()
)
export const setRentalChildrenComplete = createAction(
  '[Rental] set rental children Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const setRentalSubUnits = createAction(
  '[Rental] set rental subunit',
  props<{ rentalId: string; addedChildren: string[]; removedChildren: string[]; silent?: boolean }>()
)
export const setRentalSubUnitsComplete = createAction(
  '[Rental] set rental subunit Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const updateRentalMultiUnitSettings = createAction(
  '[Rental] Update Multi unit settings',
  props<{ settings: RentalMultiUnitSettings; rentalId: string; silent?: boolean }>()
)
export const updateRentalMultiUnitSettingsComplete = createAction(
  '[Rental] Update Multi unit settings Complete',
  props<{ update: Update<Rental> }>()
)

export const unArchiveRental = createAction('[Rental] UnArchive', props<{ rental: Rental }>())
export const unArchiveRentalComplete = createAction('[Rental] UnArchive Complete', props<{ rental: Rental }>())

export const addRentalImages = createAction(
  '[Rental] Add Images',
  props<{ rentalId: string; data: TokeetImagePayload[] }>()
)
export const addRentalImagesComplete = createAction('[Rental] Add Images Complete', props<{ images: TokeetImage[] }>())

export const removeRentalImage = createAction('[Rental] Remove Image', props<{ id: string }>())
export const removeRentalImageComplete = createAction('[Rental] Remove Image Complete', props<{ image: TokeetImage }>())

export const removeRentalImages = createAction('[Rental] Remove Images', props<{ ids: string[] }>())
export const removeRentalImagesComplete = createAction(
  '[Rental] Remove Images Complete',
  props<{ images: TokeetImage[] }>()
)

export const makeRentalImagePrimary = createAction(
  '[Rental] Make Image Primary',
  props<{ rentalId: string; primaryImageId: string; images: TokeetImage[] }>()
)

export const editRentalImage = createAction(
  '[Rental] Edit Image',
  props<{ imageId: string; category?: number; description?: string }>()
)
export const editRentalImageComplete = createAction('[Rental] Edit Image Complete', props<{ image: TokeetImage }>())

export const updateRental = createAction('[Rental] Update', props<{ payload: UpdateRentalPayload; rentalId: string }>())
export const updateRentalComplete = createAction('[Rental] Update Complete', props<{ update: Update<Rental> }>())

export const syncRentalEntityTags = createAction('[Rental] Sync Entity Tags', props<{ updates: Update<Rental>[] }>())

export const updateMultipleRentalsComplete = createAction(
  '[Rental] Update Multiple Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const updateRentalImageOrder = createAction(
  '[Rental] Update Image Order',
  props<{ images: { [key: string]: number }; rental: Rental }>()
)
export const updateRentalImageOrderComplete = createAction(
  '[Rental] Update Image Order Complete',
  props<{ update: Update<Rental> }>()
)

export const updateRentalTaxes = createAction(
  '[Rental] Update Taxes',
  props<{ taxes: TaxV3[]; rentalId: string; silent?: boolean }>()
)
export const updateRentalTaxesComplete = createAction(
  '[Rental] Update Taxes Complete',
  props<{ update: Update<Rental> }>()
)

export const updateRentalFeesComplete = createAction('[Rental] Update Fees Complete')

export const updateRentalPaymentSchedule = createAction(
  '[Rental] Update Payment Schedule',
  props<{ payload: RentalPaymentSchedule; rentalId: string; silent?: boolean }>()
)
export const updateRentalPaymentScheduleComplete = createAction(
  '[Rental] Update Payment Schedule Complete',
  props<{ update: Update<Rental> }>()
)

export const updateRentalCustomFields = createAction(
  '[Rental] Update Rental Custom',
  props<{ payload: UpdateRentalCustomPayload; rentalId: string }>()
)
export const updateRentalCustomFieldsComplete = createAction(
  '[Rental] Update Rental Custom Complete',
  props<{ update: Update<Rental> }>()
)

export const updateRentalDetails = createAction(
  '[Rental] Update Rental Details',
  props<{ payload: UpdateRentalDetailsPayload; rentalId: string }>()
)
export const updateRentalDetailsComplete = createAction(
  '[Rental] Update Rental Details Complete',
  props<{ update: Update<Rental> }>()
)

export const updateRentalInstructions = createAction(
  '[Rental] Update Rental Instructions',
  props<{ payload: UpdateRentalInstructionsPayload; rentalId: string }>()
)
export const updateRentalInstructionsComplete = createAction(
  '[Rental] Update Rental Instructions Complete',
  props<{ update: Update<Rental> }>()
)

export const updateRentalBaseRate = createAction(
  '[Rental] Update Rental Base Rate',
  props<{ payload: UpdateRentalBaseRateSettingsPayload; rentalId: string }>()
)
export const updateRentalBaseRateComplete = createAction(
  '[Rental] Update Rental Base Rate Complete',
  props<{ update: Update<Rental> }>()
)

export const updateRentalSecurityDepositConfig = createAction(
  '[Rental] Update Security Deposit Config',
  props<{ rentalId: string; config: RentalSecurityDepositConfig | null }>()
)

export const copyRentalBookingFees = createAction(
  '[Rental] Copy Rental Booking Fees',
  props<{ srcRentalId: string; dstRentalIds: string[]; resetDstRentals?: boolean; silent?: boolean }>()
)
export const copyRentalBookingFeesComplete = createAction('[Rental] Copy Rental Booking Fees Complete')

export const copyRentalImages = createAction(
  '[Rental] copy rental images',
  props<{ srcRentalId: string; dstRentalIds: string[]; resetDstRentals?: boolean; silent?: boolean }>()
)
export const copyRentalImagesComplete = createAction('[Rental] copy rental images Complete')

export const updateRentalOwners = createAction(
  '[Rental] Update Owners',
  props<{ rental: Rental; addOwnerIds: string[]; removeOwnerIds: string[]; currentOwnerIds: string[] }>()
)
export const updateRentalOwnersComplete = createAction(
  '[Rental] Update Owners Complete',
  props<{ update: Update<Rental> }>()
)

export const toggleOwnerRentals = createAction(
  '[Rental] Toggle Owner Rentals',
  props<{ ownerId: string; rentals: Rental[]; selected: boolean }>()
)
export const toggleOwnerRentalsComplete = createAction(
  '[Rental] Toggle Owner Rentals Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const restrictRentalUsers = createAction(
  '[Rental] Restrict Users',
  props<{ rental: Rental; addUserIds: string[]; removeUserIds: string[]; currentUserIds: string[] }>()
)
export const restrictRentalUsersComplete = createAction(
  '[Rental] Restrict Users Complete',
  props<{ update: Update<Rental> }>()
)

export const loadSubdomain = createAction('[Rental] Load SubDomain')
export const loadSubdomainComplete = createAction('[Rental] Load SubDomain Complete', props<{ domain: string }>())

export const addTagsToRental = createAction(
  '[Tag] Add Tags To Rental',
  props<{ rental: Rental; tags: string[]; newTags: string[] }>()
)
export const addTagsToRentalComplete = createAction(
  '[Tag] Add Tags To Rental Complete',
  props<{ update: Update<Rental> }>()
)

export const restrictUser = createAction('[Rental] Restrict User', props<{ rental: Rental; userId: string }>())
export const restrictUserComplete = createAction('[Rental] Restrict User Complete', props<{ update: Update<Rental> }>())

export const restrictUserAll = createAction(
  '[Rental] Restrict User All',
  props<{ rentals: Rental[]; userId: string }>()
)
export const restrictUserAllComplete = createAction(
  '[Rental] Restrict User All Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const unRestrictUser = createAction('[Rental] UnRestrict User', props<{ rental: Rental; userId: string }>())
export const unRestrictUserComplete = createAction(
  '[Rental] UnRestrict User Complete',
  props<{ update: Update<Rental> }>()
)

export const unRestrictUserAll = createAction(
  '[Rental] UnRestrict User All',
  props<{ rentals: Rental[]; userId: string }>()
)
export const unRestrictUserAllComplete = createAction(
  '[Rental] UnRestrict User All Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const updateRentalGPS = createAction('[Rental] Update GPS', props<{ gps: any; rentalId: string }>())
export const updateRentalGPSComplete = createAction('[Rental] Update GPS Complete', props<{ update: Update<Rental> }>())

export const updateAllRentalsCheckTimes = createAction(
  '[Rental] Update All Rentals Checkin/Checkout',
  props<{ checkin: RentalCheckTime; checkout: RentalCheckTime }>()
)
export const updateAllRentalsCheckTimesComplete = createAction(
  '[Rental] Update All Rentals Checkin/Checkout Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const removeRateMappingForChannel = createAction(
  '[Rental] Remove Rate Mapping for Channel',
  props<{ channelId: string }>()
)

export const UpdateRentalStatus = createAction(
  '[Rental] Update Status',
  props<{ rentalId: string; status: RentalStatus }>()
)
export const UpdateRentalStatusComplete = createAction(
  '[Rental] Update Status Complete',
  props<{ update: Update<Rental> }>()
)

export const UpdateRentalsStatus = createAction(
  '[Rental] Update Rentals Status',
  props<{ rentalIds: string[]; status: RentalStatus }>()
)
export const UpdateRentalsStatusComplete = createAction(
  '[Rental] Update Rentals Status Complete',
  props<{ updates: Update<Rental>[] }>()
)

export const UpdateSubunitRentals = createAction(
  '[Rental] Update Subunit Rentals',
  props<{ items: { id: string; payload: UpdateSubunitRentalPayload }[] }>()
)
export const UpdateSubunitRentalsComplete = createAction(
  '[Rental] Update Subunit Rentals Complete',
  props<{ updates: Update<Rental>[] }>()
)
